import React, { type Dispatch, type FC, type SetStateAction } from 'react';
import { Portal } from 'react-portal';
import { Redirect } from 'react-router-dom';
import { FormValues, useFormField } from '@ynap/form';
import Overlay, { StopPropagation } from '@ynap/overlay';
import { AddressForm } from '../AddressForm';
import { Locale, Config, Messages, CheckoutAnalytics } from '../AddressForm/@types';
import { EditAddressAction } from './@types';
import { mapAddressValues } from '../AddressForm/utils';
import { TrackingProp } from 'react-tracking';

type AnyFn = (...args: any[]) => unknown;
export interface EditAddressRouteProps {
    config: Config;
    locale: Locale;
    messages: Messages;
    addresses: Array<FormValues>;
    match: { path: string; url: string };
    editMatch: { path: string; url: string; params: { addressId: string } };
    history: { push: (url: string) => void };
    editAddress?: EditAddressAction;
    editTitle?: string;
    trackUpdateAddress?: AnyFn;
    isCheckoutLayout?: boolean;
    withOverlay?: boolean;
    hideCountry?: boolean;
    tracking?: TrackingProp;
    primaryCategory?: string;
    checkoutAnalytics?: CheckoutAnalytics;
    removeAddressFinder?: (...args: any[]) => any;
    setRemoveAddressFinder?: Dispatch<SetStateAction<() => any>>;
}

const EditAddressRoute: FC<EditAddressRouteProps> = ({
    config,
    locale,
    messages,
    addresses,
    match,
    editMatch,
    history,
    editAddress,
    editTitle,
    trackUpdateAddress,
    hideCountry,
    isCheckoutLayout,
    withOverlay,
    tracking,
    primaryCategory,
    checkoutAnalytics,
    removeAddressFinder,
    setRemoveAddressFinder,
}) => {
    const address = addresses.find((address: FormValues) => address.addressId.toString() === editMatch?.params?.addressId);
    const [addressIdField, setAddressIdField] = useFormField('addressId');

    const handleSubmit = (values: FormValues) => {
        if (editAddress) {
            const mappedValues = mapAddressValues(values, locale);
            trackUpdateAddress?.();
            return editAddress(mappedValues).then(({ payload: { addressId } }) => {
                // Need to update the currently selected address
                setAddressIdField({ ...addressIdField, value: addressId });
                history.push(match?.url);
            });
        }
    };

    const renderContent = () => {
        return (
            <StopPropagation>
                <AddressForm
                    config={config}
                    country={locale.country}
                    initialValues={address}
                    onSubmit={handleSubmit}
                    messages={messages}
                    touched={true}
                    isCheckoutLayout={isCheckoutLayout}
                    hideCountry={hideCountry}
                    tracking={tracking}
                    primaryCategory={primaryCategory}
                    checkoutAnalytics={checkoutAnalytics}
                    removeAddressFinder={removeAddressFinder}
                    setRemoveAddressFinder={setRemoveAddressFinder}
                />
            </StopPropagation>
        );
    };

    if (address) {
        if (withOverlay) {
            return (
                <Portal>
                    <Overlay title={editTitle} onClose={() => history.push(match.url)}>
                        {renderContent()}
                    </Overlay>
                </Portal>
            );
        }

        return renderContent();
    }

    return <Redirect to={match.url} />;
};

export default EditAddressRoute;
